import { useCallback, useEffect, useState } from 'react';
import { validateFile } from '@/utils/nodeInputValidation';
import { useUserWorkflowRole } from '@/state/workflow.state';
import { ImageIteratorIcon } from '@/UI/Icons';
import { NodeViewMode } from '@/enums/node-view-mode.enum';
import { useDeleteAssetsSubMenu } from '@/hooks/useDeleteAssetsSubMenu';
import { FileUploaderV2 } from '../../Common/FileUploaderV2';
import { useMediaIteratorView, useEffectiveIsLocked } from '../../Recipe/FlowContext';
import { DynamicNode2 } from '../DynamicNode/DynamicNode2';
import { ImportNodeViewer } from './../ImportNode/ImportNodeViewer';
import type { MediaIteratorData } from '@/types/node';
import type { MediaAsset } from '@/types/api/assets';
import type { FileType } from '@/hooks/useUploadMediaAsset';

function MediaIteratorNode({ id, data }: { id: string; data: MediaIteratorData }) {
  const role = useUserWorkflowRole();
  const mediaIteratorView = useMediaIteratorView(id);
  const [isUploadFormUploading, setIsUploadFormUploading] = useState(false);
  const { pastedData, initialData, selectedIndex, viewMode = NodeViewMode.Single } = data;
  const files = mediaIteratorView.getFilesList();
  const displayViewMode = files.length === 1 ? NodeViewMode.Single : viewMode;
  const isLocked = useEffectiveIsLocked(id);
  const editable = !isLocked && role === 'editor';

  const selectedFile = files[selectedIndex];

  const onUpload = useCallback(
    (file: Partial<MediaAsset> | null) => {
      const fileKind = validateFile(file);
      if (fileKind) {
        mediaIteratorView.addFile(fileKind, false);
      }
    },
    [mediaIteratorView],
  );

  useEffect(() => {
    if (initialData) {
      const fileKind = validateFile(initialData);
      if (fileKind) {
        mediaIteratorView.setFiles([fileKind], undefined, false);
      }
    }
  }, [id, mediaIteratorView, initialData]);

  const onInitialFileUpload = useCallback(
    (hadError: boolean) => {
      if (hadError) {
        mediaIteratorView.clearPastedAndInitialData();
      }
    },
    [mediaIteratorView],
  );

  const deleteAssetsMenuItems = useDeleteAssetsSubMenu({
    nodeId: id,
    view: {
      deleteAll: () => mediaIteratorView.deleteAll(),
      deleteAllOthers: () => mediaIteratorView.deleteAllOthers(),
      deleteCurrent: () => mediaIteratorView.deleteCurrent(),
      assets: files,
    },
  });

  return (
    <DynamicNode2
      id={id}
      data={data}
      icon={<ImageIteratorIcon />}
      className="import"
      additionalMenu={deleteAssetsMenuItems}
    >
      {files.length > 0 && selectedFile && !isUploadFormUploading ? (
        <ImportNodeViewer
          id={id}
          files={files}
          selectedIndex={selectedIndex}
          onUpload={onUpload}
          viewMode={displayViewMode}
          selectedViewMode={viewMode}
        />
      ) : (
        <FileUploaderV2
          allowUrlInput
          disabled={!editable}
          id={id}
          initialFile={(pastedData?.imageFile as File) || undefined}
          onUpload={onUpload}
          value={selectedFile}
          isFileValid={(file) => {
            return validateFile(file) !== undefined;
          }}
          setIsUploading={setIsUploadFormUploading}
          isUploading={isUploadFormUploading}
          onInitialFileUpload={onInitialFileUpload}
          acceptedFileType={data.acceptedFileType as FileType}
        />
      )}
    </DynamicNode2>
  );
}

export default MediaIteratorNode;
